import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import classnames from "classnames";

import { DayPicker } from "react-day-picker";
import propTypes from "prop-types";
import InputMask from "react-input-mask"; //https://www.npmjs.com/package/react-input-mask-for-date

import { createDataAttributes } from "../../utilities";
import { isValidDate } from "../../utilities/dates";
import { useField } from "../form/Form";
import { formatDate } from "../formatDate/FormatDate";
import ErrorsForField from "../form/ErrorsForField";
import Icon from "../icons/Icon";
import Label from "./Label";

import "./DatePicker.scss";

const Calendar = ({
  value,
  handleDaySelect,
  setShow,
  mainRef,
  minDate,
  maxDate
}) => {
  const defaultMonth = isValidDate(value) ? new Date(value) : undefined;

  const [pos, setPos] = useState({});
  const [currentMonth, setCurrentMonth] = useState(defaultMonth);

  useEffect(() => {
    if (isValidDate(value)) setCurrentMonth(new Date(value));
  }, [value]);

  const popperRef = useRef(null);

  // handle clicking outside of the popper to close the popper
  useEffect(() => {
    const handleClickOutside = event => {
      if (mainRef.current && !mainRef.current.contains(event.target))
        setShow(false);
    };
    const escKey = evt => {
      evt = evt || window.event;
      if (evt.keyCode === 27) setShow(false);
    };
    document.addEventListener("keydown", escKey, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("keydown", escKey, true);
    };
  }, [mainRef, setShow]);

  // handle the position of the popper in case it doesn't fit in the viewport when the component renders
  useLayoutEffect(() => {
    const mainRect = mainRef?.current
      ? mainRef.current.getBoundingClientRect()
      : {};

    const popperRect = popperRef?.current
      ? popperRef.current.getBoundingClientRect()
      : {};

    const popperHeight = popperRect?.height;
    const spaceBelow = window.innerHeight - (mainRect?.bottom || 0);
    const spaceRight = window.innerWidth - (mainRect?.right || 0);
    const popperWidth = popperRect?.width;
    const showAbove = popperHeight && spaceBelow < popperHeight;
    const showLeft = popperWidth && spaceRight < popperWidth;

    if (mainRect && popperRect)
      setPos({
        top: showAbove
          ? "-" + (popperRect.height + 5) + "px"
          : mainRect.height + 10 + "px",
        right: showLeft ? 0 : "auto",
        showAbove,
        showLeft
      });
  }, [mainRef]);

  const classes = classnames("uikit__datepicker__calendar-wrapper", {
    uikit__datepicker__calendar__showAbove: pos?.showAbove,
    uikit__datepicker__calendar__showLeft: pos?.showLeft
  });

  const selectedDate = isValidDate(value) ? formatDate(value) : undefined;

  const dataPos = [
    pos?.showAbove ? "above" : "below",
    pos?.showLeft ? "left" : "right"
  ];


  return (
    <div
      ref={popperRef}
      className={classes}
      style={pos}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Date Picker Calendar"
    >
      <DayPicker
        data-pos={dataPos.join("-")}
        mode="single"
        className="uikit__datepicker__calendar"
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        selected={selectedDate}
        onSelect={handleDaySelect}
        disabled={day =>
          (minDate && day < new Date(minDate)) ||
          (maxDate && day > new Date(maxDate))
        }
      />
    </div>
  );
};

const DatePickerComponent = ({
  id,
  name,
  label,
  labelElement,
  value,
  maxDate,
  minDate,
  startDate,
  disabled,
  onChange,
  onBlur,
  onValidate,
  onKeyDown,
  required,
  format,
  hideLabel,
  noLabel,
  requiredError,
  size,
  fieldErrors,
  hideErrors,
  className,
  dataAttrib,
  dataWrapperAttrib,
  placeholder,
  ...rest
}) => {
  const mainRef = useRef(null);

  const [show, setShow] = useState();

  const min = minDate ? new Date(minDate).setHours(0, 0, 0, 0) : null;
  const max = maxDate ? new Date(maxDate).setHours(0, 0, 0, 0) : null;


  useEffect(() => {
    /*
    when the component loads, if the initial value doesn't match
    the output 'format' then update the value to the format
    so if the user doesn't change it, the output is what the form expects
    -- the assumption is that the incoming date format will match
    the 'format' setting on the component, but if it doesn't
    then this will force it to be that output format
    */
    if (value !== formatDate(value, format)) {
      onChange({
        event: null,
        field: name,
        value: formatDate(value, format)
      });
    }

    // we only want this to run at the intial component load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
  handle custom component validation

  validation to allow:
  - an empty field
  - a valid date formatted as 2019-12-30
  */
  const handleOnValidate = ({ field, value, validation }) => {
    const errors = onValidate({ field, value, validation });

    if (!value) return errors;

    // const regTest =
    //   /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$|^$/;
    const l = label || "This";
    const epoch = minDate || maxDate ? formatDate(value, "epoch") : null;
    const userVal = value;

    if (!isValidDate(value)) {
      errors.push(l + " is not a valid date.");
    }
    if (min && userVal.length && epoch < min)
      errors.push(
        l + " cannot be before " + formatDate(minDate, "M/D/YYYY") + "."
      );
    if (max && userVal.length && epoch > max)
      errors.push(
        l + " cannot be after " + formatDate(maxDate, "M/D/YYYY") + "."
      );

    return errors;
  };

  useField({
    name,
    validation: { required, requiredError },
    onValidate: handleOnValidate,
    label
  });

  const handleOnFocus = () => setShow(true);

  const handleOnBlur = event => {
    onBlur({
      field: name,
      value: formatDate(event.target.value, format)
    });
  };

  const handleOnChange = event => {
    const raw = event.target.value;
    const v = isValidDate(raw) ? formatDate(raw, format) : raw;

    onChange({
      field: name,
      value: v,
      callback: () => {}
    });
  };

  const handleClear = () => {
    onChange({
      field: name,
      value: ""
    });
    setShow(false);
  };

  const handleDaySelect = date => {
    if (date)
      onChange({
        field: name,
        value: formatDate(date, format),
        callback: () => {
          onBlur({
            field: name,
            value: formatDate(date, format)
          });
        }
      });
    setShow(false);
  };

  const sizeClass = size ? "uikit__datepicker-size__" + size : null;
  const classes = classnames(
    "uikit__datepicker",
    "uikit__form-item",
    className,
    {
      "uikit__hide-label": hideLabel,
      "uikit__form__hide-errors": hideErrors,
      "uikit__datepicker__has-value": !!value
    }
  );

  const inputClasses = classnames("uikit__datepicker__input", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value
  });

  const handleKeyDown = e => {
    if (e.key === "Tab") setShow(false);
  };

  const v = isValidDate(value) ? formatDate(value, "MM/DD/YYYY") : value;

  return (
    <div
      className={classnames(classes, sizeClass)}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      <div className="uikit__datepicker-wrapper" ref={mainRef}>
        {!noLabel && (
          <Label
            htmlFor={id}
            label={label}
            labelElement={labelElement}
            required={required}
          />
        )}
        <div className="uikit__datepicker-inner">
          <div className="uikit__datepicker__input-wrapper">
            <InputMask
              mask="99/99/9999"
              id={id}
              name={name}
              value={v}
              disabled={disabled}
              onFocus={handleOnFocus}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
              onKeyDown={handleKeyDown}
              className={inputClasses}
              placeholder={placeholder}
              {...createDataAttributes(dataAttrib)}
              {...rest}
            />
            {!!value && !disabled && (
              <>
                <div id={`datepicker-clear-${id}`} className="uikit-sr-only">
                  Clear
                </div>
                <button
                  aria-labelledby={`datepicker-clear-${id}`}
                  tabIndex="-1"
                  type="button"
                  className="uikit__datepicker__clear"
                  onClick={() => handleClear()}
                >
                  <Icon icon="fasTimes" />
                </button>
              </>
            )}
          </div>
        </div>
        {show && (
          <Calendar
            mode="single"
            setShow={setShow}
            value={v}
            mainRef={mainRef}
            handleDaySelect={handleDaySelect}
            minDate={min}
            maxDate={max}
          />
        )}
      </div>

      {!show && !hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

export default DatePickerComponent;

DatePickerComponent.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.instanceOf(Date),
    propTypes.object
  ]),
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  requiredError: propTypes.string,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  minDate: propTypes.oneOfType([
    propTypes.string,
    propTypes.instanceOf(Date),
    propTypes.object
  ]),
  maxDate: propTypes.oneOfType([
    propTypes.string,
    propTypes.instanceOf(Date),
    propTypes.object
  ]),
  startDate: propTypes.oneOfType([
    propTypes.string,
    propTypes.instanceOf(Date),
    propTypes.object
  ]),
  format: propTypes.string,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  placeholder: propTypes.string
};

DatePickerComponent.defaultProps = {
  placeholder: "MM/DD/YYYY",
  format: "MM/DD/YYYY",
  hideErrors: false,
  componentname: "DatePicker"
};
